import { useEffect } from 'react';
import { api } from './lib/api';
import { refreshCatalog } from './lib/catalog';
import { match, Redirect, useLocation, useScrollReset } from './lib/router';
import { useApp } from './lib/store';
import { Toast } from './components/Shell';
import { Splash } from './components/Brand';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Onboarding } from './pages/Onboarding';
import { Home } from './pages/Home';
import { Explore } from './pages/Explore';
import { ShowPage } from './pages/Show';
import { Watch } from './pages/Watch';
import { Reels } from './pages/Reels';
import { Approve, Paywall, PlusDone } from './pages/Plus';
import { Account } from './pages/Account';
import { Notifications } from './pages/Notifications';
import { Library } from './pages/Library';
import { TopicPage, TopicsIndex } from './pages/Topic';
import { CompanyPage } from './pages/Company';

function Routes() {
  const { path } = useLocation();
  const session = useApp((s) => s.session);
  useScrollReset(path);
  let m: Record<string, string> | null;

  if (path === '/') return session ? <Redirect to="/home" /> : <Landing />;
  if (path === '/welcome') return <Landing />;
  if (path === '/topics') return <TopicsIndex />;
  if ((m = match('/topic/:slug', path))) return <TopicPage key={m.slug} slug={m.slug} />;
  if ((m = match('/company/:slug', path))) return <CompanyPage key={m.slug} slug={m.slug} />;
  if (path === '/login') return <Login />;
  if (path === '/onboarding') return session ? <Onboarding /> : <Redirect to="/login" />;
  if (path === '/home') return <Home />;
  if (path === '/explore') return <Explore />;
  if ((m = match('/explore/:topic', path))) return <Explore topic={m.topic} />;
  if ((m = match('/show/:slug', path))) return <ShowPage key={m.slug} slug={m.slug} />;
  if ((m = match('/watch/:show/:no', path))) return <Watch key={m.show + m.no} showId={m.show} no={Number(m.no)} />;
  if (path === '/reels') return <Reels />;
  if (path === '/plus') return <Paywall />;
  if (path === '/plus/approve') return <Approve />;
  if (path === '/plus/done') return <PlusDone />;
  if (path === '/account') return <Account />;
  if (path === '/notifications') return session ? <Notifications /> : <Redirect to="/login?next=/notifications" />;
  if (path === '/library') return <Library />;
  return (
    <div className="page narrow center-stack">
      <h1 className="page-title">Page not found</h1>
      <p className="muted">That link does not go anywhere.</p>
      <a href="#/home" className="btn btn-primary">Go home</a>
    </div>
  );
}

export function App() {
  // Live API: restore the session and refresh the catalogue on start and whenever the app comes back.
  useEffect(() => {
    refreshCatalog();
    api.bootstrap();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshCatalog(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  const scale = useApp((s) => s.settings.textScale);
  const theme = useApp((s) => s.settings.theme);
  useEffect(() => {
    document.documentElement.style.setProperty('--text-scale', String(scale / 100));
  }, [scale]);
  // Light / dark: "Phone setting" follows the device and updates live when it changes.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    const apply = () => {
      const resolved = theme === 'system' ? (mq?.matches ? 'light' : 'dark') : theme;
      document.documentElement.dataset.theme = resolved;
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', resolved === 'light' ? '#FFFDF8' : '#141414');
    };
    apply();
    mq?.addEventListener?.('change', apply);
    return () => mq?.removeEventListener?.('change', apply);
  }, [theme]);
  return (
    <>
      <Routes />
      <Toast />
      <Splash />
    </>
  );
}
